import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Ticket,
  CheckCircle,
  Clock,
  TrendingUp,
  Shield,
  Activity,
  AlertTriangle,
  Plus,
  UserPlus,
  BarChart3,
  RefreshCcw,
  Zap,
  Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { allUsers, refreshAllUsers } = useAuth();
  const { tickets, refreshTickets } = useTickets();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (refreshTickets) await refreshTickets(false);
      // AuthContext handles allUsers via real-time, but we can add a manual fetch if it exists
      if (typeof refreshAllUsers === 'function') await refreshAllUsers();
      toast.success('Dados atualizados com sucesso!');
    } catch (err) {
      toast.error('Erro ao atualizar dados.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Helpers de Tempo (24 Horas)
  const isWithin24h = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    return diff <= 24 * 60 * 60 * 1000;
  };

  // Estatísticas Reais com Foco em 24h
  const totalRegistered = allUsers?.filter(u => u.registrationStatus === 'registered')?.length || 0;
  const activeUsers24h = allUsers?.filter(u => isWithin24h(u.lastLoginAt || u.lastLogin))?.length || 0;
  const activeStaff24h = allUsers?.filter(u => u.accessLevel !== 'user' && isWithin24h(u.lastLoginAt || u.lastLogin))?.length || 0;

  // Filtros de Tickets 24h
  const tickets24h = (tickets || []).filter(t => isWithin24h(t.createdAt) || isWithin24h(t.lastUpdate));

  const openNoResponse24h = (tickets || []).filter(t =>
    t.status === 'aberto' && (!t.responses || t.responses.filter(r => r && r.isStaff).length === 0)
  ).length;

  const inProgressWithStaff24h = (tickets || []).filter(t =>
    (t.status === 'em_andamento' || t.status === 'aberto' || t.status === 'aguardando_usuario') &&
    (t.responses && t.responses.some(r => r && r.isStaff))
  ).length;

  const finished24h = tickets24h.filter(t =>
    (t.status === 'resolvido' || t.status === 'fechado')
  ).length;

  const resolutionRate24h = tickets24h.length > 0
    ? ((finished24h / tickets24h.length) * 100).toFixed(1)
    : '100';

  const stats = [
    {
      label: 'Usuários Cadastrados',
      value: totalRegistered.toLocaleString(),
      trend: 'Geral',
      icon: Users,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/20',
      description: 'Total de jogadores com registro completo'
    },
    {
      label: 'Jogadores Ativos (24h)',
      value: activeUsers24h.toLocaleString(),
      trend: 'Ao Vivo',
      icon: Zap,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      description: 'Usuários que logaram nas últimas 24h'
    },
    {
      label: 'Staff Online (24h)',
      value: activeStaff24h.toLocaleString(),
      trend: 'Equipe',
      icon: Shield,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      description: 'Membros da staff ativos recentemente'
    },
    {
      label: 'Pendentes (24h)',
      value: openNoResponse24h.toLocaleString(),
      trend: 'Crítico',
      icon: Target,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/20',
      description: 'Tickets sem nenhuma resposta staff'
    },
    {
      label: 'Em Atendimento (24h)',
      value: inProgressWithStaff24h.toLocaleString(),
      trend: 'Interação',
      icon: Activity,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/20',
      description: 'Tickets com interação ativa da staff'
    },
    {
      label: 'Concluídos (24h)',
      value: finished24h.toLocaleString(),
      trend: 'Sucesso',
      icon: CheckCircle,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/20',
      description: 'Tickets marcados como resolvidos'
    }
  ];

  return (
    <div className="space-y-8 pb-12 animate-in fade-in duration-700">
      {/* Dynamic Background Effect */}
      <div className="fixed inset-0 pointer-events-none opacity-50">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-red-500/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-orange-500/10 blur-[100px] rounded-full animate-pulse delay-700" />
      </div>

      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-red-600/20 rounded-2xl border border-red-500/30">
              <Shield className="h-6 w-6 text-red-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
              SISTEMA <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500">CAPITAL</span>
            </h1>
          </div>
          <p className="text-gray-400 text-lg flex items-center gap-2 font-medium">
            <Activity className="h-4 w-4 text-emerald-500 animate-pulse" />
            Painel de Controle Estratégico • {currentTime.toLocaleDateString('pt-BR')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-14 px-6 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-2xl transition-all group backdrop-blur-md"
          >
            <RefreshCcw className={`h-5 w-5 mr-3 transition-transform duration-700 ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
            <span className="font-black uppercase tracking-widest text-xs">Atualizar Dados</span>
          </Button>

          <div className="h-14 px-6 bg-black/60 backdrop-blur-xl rounded-2xl border border-white/10 flex flex-col justify-center">
            <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-black">Horário Local</p>
            <p className="text-xl font-mono font-black text-white leading-none mt-1">
              {currentTime.toLocaleTimeString('pt-BR')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 relative z-10">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ y: -8, transition: { duration: 0.2 } }}
          >
            <Card className={`group bg-black/40 backdrop-blur-md border ${stat.border} overflow-hidden rounded-[2rem] transition-all duration-500 h-full`}>
              <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.08] transition-opacity">
                <stat.icon size={120} />
              </div>

              <CardContent className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className={`p-4 rounded-[1.25rem] ${stat.bg} border border-white/5`}>
                    <stat.icon className={`h-8 w-8 ${stat.color}`} />
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[10px] font-black uppercase tracking-widest text-gray-400">
                    <Target className="h-3 w-3" />
                    {stat.trend}
                  </div>
                </div>

                <div className="space-y-1">
                  <h3 className="text-gray-400 text-xs font-black uppercase tracking-[0.15em]">{stat.label}</h3>
                  <div className="flex items-baseline gap-3">
                    <span className="text-5xl font-black text-white tracking-tighter">{stat.value}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 font-medium pt-2 italic">
                    {stat.description}
                  </p>
                </div>
              </CardContent>

              <div className={`absolute bottom-0 left-0 h-1.5 bg-gradient-to-r ${stat.bg.replace('bg-', 'from-').replace('/10', '')} to-transparent w-full opacity-0 group-hover:opacity-100 transition-opacity`} />
            </Card>
          </motion.div>
        ))}

        {/* Efficiency Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="md:col-span-2 xl:col-span-3"
        >
          <Card className="bg-gradient-to-r from-red-600/20 via-orange-600/10 to-transparent border-red-500/20 rounded-[2.5rem] overflow-hidden">
            <CardContent className="p-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <BarChart3 className="text-orange-400 h-6 w-6" />
                  <h4 className="text-xl font-bold text-white tracking-tight">Performance Global do Sistema</h4>
                </div>
                <p className="text-gray-400 max-w-xl">
                  Análise de eficiência baseada no volume de tickets resolvidos em relação ao total de chamados abertos nas últimas 24 horas.
                </p>
              </div>

              <div className="flex flex-col items-center">
                <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-500 leading-none">
                  {resolutionRate24h}%
                </div>
                <span className="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] mt-2 text-center">TAXA DE RESOLUÇÃO</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;
